import {
  DatasourceType,
  PolicyStatusType,
  PolicyAmountType,
  PeriodType,
  InclusionDateType,
  PolicyFilter,
  PeriodOutput,
  ActivityFilterOutput,
  UserFiltersOutput,
  PercentileFilterOutput,
  LocationFilterOutput,
  ProductPolicyFilterOutput,
  PolicySourceFilterOutput,
  FilterRangeOutput,
  PolicyEntity,
  PolicyMetric,
  UserIdAndSourceCodesOutput,
  PolicyStatusFilterOutput,
  ActivityDateRangeOutput,
  ActivityCompanyFilterOutput,
  ProductActivityFilterOutput,
} from 'modules/decisionsNext/api/types';
import {StoredFilter} from 'filters/features/types';

import {formatDateToString} from 'utils/dateUtils';
import {FilterOutputFragment} from 'features/GlobalFilterProvider/api/policyFilters.generated';

const todayString = formatDateToString();

export const savedFilterPresets: StoredFilter[] = [
  {
    id: 'filter-preset-inforce',
    name: 'Inforce: Inforce, ennabl written',
    lastUsed: new Date().toISOString(),
    filter: {
      _type: 'policy' as const,
      datasourceType: DatasourceType.ProductionCredit,
      policyStatusType: PolicyStatusType.Ennabl,
      period: {
        amountType: PolicyAmountType.Written,
        type: PeriodType.InForce,
        timestamp: null,
      },
    },
  },
  {
    id: 'filter-preset-year-to-date',
    name: 'Year-to-date: Effective YTD, ennabl written',
    lastUsed: new Date().toISOString(),
    filter: {
      _type: 'policy' as const,
      datasourceType: DatasourceType.ProductionCredit,
      policyStatusType: PolicyStatusType.Ennabl,
      period: {
        amountType: PolicyAmountType.Written,
        type: PeriodType.Ytd,
        inclusionDateType: InclusionDateType.Inception,
        timestamp: todayString,
      },
    },
  },
  {
    id: 'filter-preset-rolling-12',
    name: 'Rolling 12: Rolling 12, billed',
    lastUsed: new Date().toISOString(),
    filter: {
      _type: 'policy' as const,
      datasourceType: DatasourceType.ProductionCredit,
      policyStatusType: PolicyStatusType.Ennabl,
      period: {
        amountType: PolicyAmountType.Billed,
        type: PeriodType.Rolling_12Months,
        inclusionDateType: InclusionDateType.Transaction,
        timestamp: todayString,
      },
    },
  },
];

const periodOutput: Required<PeriodOutput> = {
  __typename: 'PeriodOutput',
  amountType: null,
  from: null,
  type: null,
  timestamp: null,
  to: null,
  inclusionDateType: null,
};

const filterRangeOutput: Required<FilterRangeOutput> = {
  __typename: 'FilterRangeOutput',
  from: null,
  to: null,
  mode: null,
  notExists: null,
};

const activityDateRangeOutput: Required<ActivityDateRangeOutput> = {
  __typename: 'ActivityDateRangeOutput',
  dateRangeType: null,
  from: null,
  to: null,
  period: null,
};

const activityCompanyFilterOutput: Required<ActivityCompanyFilterOutput> = {
  __typename: 'ActivityCompanyFilterOutput',
  childIds: null,
  parentIds: null,
  ultimateParentIds: null,
};

const productActivityFilterOutput: Required<ProductActivityFilterOutput> = {
  __typename: 'ProductActivityFilterOutput',
  productIds: null,
  productGroupIds: null,
  productSuperGroupIds: null,
  productInsuranceSegmentIds: null,
};

const activityFilterOutput: Required<ActivityFilterOutput> = {
  __typename: 'ActivityFilterOutput',
  activityCodes: null,
  dateRange: null,
  employeeIds: null,
  hasClient: null,
  isOverdue: null,
  carrierFilter: null,
  productFilter: null,
};

const userIdAndSourceCodesOutput: Required<UserIdAndSourceCodesOutput> = {
  __typename: 'UserIdAndSourceCodesOutput',
  sourceCodes: null,
  userId: null as any as string,
};

const userFiltersOutput: Required<UserFiltersOutput> = {
  __typename: 'UserFiltersOutput',
  accountExecutiveIds: null,
  accountExecutiveIdsAndSourceCodes: null,
  accountExecutiveIsEmpty: null,
  accountManagerIds: null,
  accountManagerIdsAndSourceCodes: null,
  accountManagerIsEmpty: null,
  producerIds: null,
  producerIdsAndSourceCodes: null,
  producerIsEmpty: null,
};

const percentileFilterOutput: Required<PercentileFilterOutput> = {
  __typename: 'PercentileFilterOutput',
  entity: null as any as PolicyEntity,
  metric: null as any as PolicyMetric,
  from: null,
  to: null,
};

const locationFilterOutput: Required<LocationFilterOutput> = {
  __typename: 'LocationFilterOutput',
  counties: null,
  postalCodes: null,
  states: null,
  postalCodesRadius: null,
};

const productFilterOutput: Required<ProductPolicyFilterOutput> = {
  __typename: 'ProductPolicyFilterOutput',
  productIds: null,
  productGroupIds: null,
  productSuperGroupIds: null,
  productInsuranceSegmentIds: null,
  productLineIds: null,
};

const policySourcesFilterOutput: Required<PolicySourceFilterOutput> = {
  __typename: 'PolicySourceFilterOutput',
  sources: null,
  isEmpty: null,
};

const policyStatusFilterOutput: Required<PolicyStatusFilterOutput> = {
  __typename: 'PolicyStatusFilterOutput',
  codes: null,
  statuses: null,
};

const emptyPolicyFilter = {
  __typename: 'PolicyFilterOutput',
  period: null,
  additionalPeriods: null,
  datasourceType: null,
  allLevelCarrierIds: null,
  sourceProductIds: null,
  activityFilter: null,
  accountSize: null,
  allLevelIntermediaryIds: null,
  brokerMarketStatuses: null,
  carrierIds: null,
  childIntermediaryIds: null,
  intermediaryIds: null,
  policyStatusType: null,
  userFilters: null,
  clientAgencyCodes: null,
  clientIds: null,
  clientName: null,
  companyIds: null,
  counties: null,
  departmentIds: null,
  direct: null,
  departmentLevel: null,
  industryGroupIds: null,
  industryIds: null,
  industrySubGroupIds: null,
  issuingPaperIds: null,
  parentCarrierIds: null,
  parentIntermediaryIds: null,
  policyAgencyCodes: null,
  policyIds: null,
  policySources: null,
  policyStatuses: null,
  policyStatusesAnd: null,
  productGroupIds: null,
  productIds: null,
  productInsuranceSegmentIds: null,
  productSuperGroupIds: null,
  metadata: null,
  query: null,
  renewalStatus: null,
  sourceCompanyIds: null,
  ultimateCarrierIds: null,
  ultimateIntermediaryIds: null,
  commissionPctRange: null,
  naicsSourceConfidenceScore: null,
  percentileFilter: null,
  policySourcesFilter: null,
  productFilters: null,
  location: null,
  isRenewable: null,
  isRewrite: null,
  isCancelled: null,
  isClientLost: null,
  isExpiredAndNonRenewed: null,
  isFee: null,
  isLost: null,
  retailAgencyCodes: null,
} satisfies Required<FilterOutputFragment>;

const fulfillObject = <T extends Record<string, unknown>>(output: T, incoming: Partial<T>): T => {
  return Object.keys(output).reduce((acc, key) => {
    acc[key] = incoming[key] ?? output[key];
    return acc;
  }, {} as any);
};

export const fulfillPolicyFilterFragment = (filter: PolicyFilter) => {
  return {
    ...fulfillObject(emptyPolicyFilter, filter as any),
    period: filter.period ? fulfillObject(periodOutput, filter.period) : null,
    additionalPeriods: filter.additionalPeriods
      ? filter.additionalPeriods.map(period => fulfillObject(periodOutput, period))
      : null,
    activityFilter: filter.activityFilter
      ? {
          ...fulfillObject(activityFilterOutput, filter.activityFilter),
          dateRange: filter.activityFilter.dateRange
            ? fulfillObject(activityDateRangeOutput, filter.activityFilter.dateRange)
            : null,
          carrierFilter: filter.activityFilter.carrierFilter
            ? fulfillObject(activityCompanyFilterOutput, filter.activityFilter.carrierFilter)
            : null,
          productFilter: filter.activityFilter.productFilter
            ? fulfillObject(productActivityFilterOutput, filter.activityFilter.productFilter)
            : null,
        }
      : null,
    accountSize: filter.accountSize ? fulfillObject(filterRangeOutput, filter.accountSize) : null,
    policyStatusesAnd: filter.policyStatusesAnd
      ? filter.policyStatusesAnd.map(status => fulfillObject(policyStatusFilterOutput, status))
      : null,
    userFilters: filter.userFilters
      ? {
          ...fulfillObject(userFiltersOutput, filter.userFilters),
          accountExecutiveIdsAndSourceCodes: filter.userFilters.accountExecutiveIdsAndSourceCodes
            ? filter.userFilters.accountExecutiveIdsAndSourceCodes.map(ue =>
                fulfillObject(userIdAndSourceCodesOutput, ue)
              )
            : null,
          accountManagerIdsAndSourceCodes: filter.userFilters.accountManagerIdsAndSourceCodes
            ? filter.userFilters.accountManagerIdsAndSourceCodes.map(am =>
                fulfillObject(userIdAndSourceCodesOutput, am)
              )
            : null,
          producerIdsAndSourceCodes: filter.userFilters.producerIdsAndSourceCodes
            ? filter.userFilters.producerIdsAndSourceCodes.map(p => fulfillObject(userIdAndSourceCodesOutput, p))
            : null,
        }
      : null,
    commissionPctRange: filter.commissionPctRange ? fulfillObject(filterRangeOutput, filter.commissionPctRange) : null,
    naicsSourceConfidenceScore: filter.naicsSourceConfidenceScore
      ? filter.naicsSourceConfidenceScore.map(score => fulfillObject(filterRangeOutput, score))
      : null,
    percentileFilter: filter.percentileFilter ? fulfillObject(percentileFilterOutput, filter.percentileFilter) : null,
    policySourcesFilter: filter.policySourcesFilter
      ? fulfillObject(policySourcesFilterOutput, filter.policySourcesFilter)
      : null,
    productFilters: filter.productFilters
      ? filter.productFilters.map(productFilter => fulfillObject(productFilterOutput, productFilter))
      : null,
    location: filter.location ? fulfillObject(locationFilterOutput, filter.location) : null,
  } satisfies Required<FilterOutputFragment>;
};
